import { GLAccountModel } from "models/Requisition/GLAccountModel";
import { ContractLineDetailMilestoneStatusId } from "utility/enums/contract-detail";

export interface IContractLineDetailModel {
	contractLineDetailId: number;
	contractLineId: number;
	financialYear: string;
	department: string;
	qty: number;
	estUnitCost: number;
	subTotal: number;
	vat: number;
	totalEstCost: number;
	isAttachments: boolean;
}

export interface IContractLine {
	contractLineId: number;
	programme: string;
	region: string;
	department: string;
	item: string;
	glCategory: string;
	productDescription: string;
	uom: string;
	qty: number;
	estUnitCost: number;
	subTotal: number;
	vat: number;
	totalEstCost: number;
	companyWideDepartmentId: number | null;
	isAttachments: boolean;
	itemAvailability: string;
	contractLineDetails: IContractLineDetailModel[] | null;
}

export interface IContractLineGridDetail {
	baseLineAmount: number;
	variations: number;
	contractAmount: number;
	prTotal: number;
	poTotal: number;
	unpaidExpenditure: number;
	paidExpenditure: number;
	remainingAmount: number;
	contractLines: IContractLine[];
}

export interface IContractAmount {
	baseLineAmount: number;
	variations: number;
	contractAmount: number;
	prTotal: number;
	poTotal: number;
	unpaidExpenditure: number;
	paidExpenditure: number;
	remainingAmount: number;
}

export class GLAccountDetailModel {
	glAccount: string = "";
	description: string = "";
	glCategory: string = "";
}

export class MileStoneDetail {
	milestone: string = "";
	plannedBillingDate: Date | null = null;
	percentageOfTotalAmount: number = 0;
	plannedAmount: number = 0;
}

export class AddUpdateContractLineDetail {
	financialYearId: number | null = null;
	qty: number | null = null;
	estUnitCost: number | null = null;
	contractLineDetailId: number | null = null;
}

export class ContractLineDetailModel extends AddUpdateContractLineDetail {
	financialYear: string = "";
	vat: number = 0;
	subTotal: number = 0;
	totalEstAmount: number = 0;
	totalMilestoneAmount: number | null = null;
	isCopy: boolean = false;
}

export class BaseContractLineDetail {
	contractLineId: number = -1;
	contractId: number = 0;
	programmeId: number | null = null;
	requestingDepartmentId: number | null = null;
	buyingDepartmentId: number | null = null;
	divisionId: number | null = null;
	regionId: number | null = null;
	requiredDate: Date | null = null;
	itemId: number | null = null;
	itemAvailabilityId: number | null = null;
	productDescription: string = "";
	glAccountId: number | null = null;
	fromFinancialYearId: number | null = null;
	toFinancialYearId: number | null = null;
	csdCommodityId: number | null = null;
	createdDate: Date = new Date();
	costCategoryId: number | null = null;
	projectId: number | null = null;
}

export class ContractLineModel extends BaseContractLineDetail {
	glAccountDetail: GLAccountDetailModel = new GLAccountDetailModel();
	fromFinancialYear: string = "";
	toFinancialYear: string = "";
	contractEstUnitCost: number | null = null;
	contractQty: number | null = null;
	contractVat: number = 0;
	contractSubTotal: number = 0;
	contractTotalEstCost: number = 0;
	totalMilestoneAmount: number | null = null;
	contractLineDetailList: ContractLineDetailModel[] = [];
	itemUomName?: string;
}

export class AddUpdateContractLineModel extends BaseContractLineDetail {
	qty: number = 0;
	estUnitCost: number = 0;
	contractLineDetailList: AddUpdateContractLineDetail[] = [];
}

export interface IContractLineAttachment {
	contractLineAttachmentId: number;
	documentFileName: string;
	documentDescription: string;
	documentFilePath: string;
	contractLineDetailId: number | null;
	financialYear?: string | null;
	stepNumber?: number | null;
	sequenceNumber?: number | null;
}

export class ContractLineDetailMilestone {
	contractLineDetailMilestoneId: number = -1;
	contractLineDetailId: number = 0;
	milestoneName: string = "";
	plannedStartDate: Date | null = null;
	plannedEndDate: Date | null = null;
	plannedBillingDate: Date | null = null;
	percentageOfTotalAmount: string = "";
	plannedAmount: number | null = null;
	statusId: number = ContractLineDetailMilestoneStatusId.New;
	lastActionDate?: Date;
}

// Contract line GET - company wide
export class ContractLineDetailByDeptModel {
	contractLineDetailId: number = -1;
	departmentId: number = -1;
	departmentName: string = "";
	qty: number | null = null;
	estUnitCost: number | null = null;
	contractLineId: number = -1;
	// Required for FE
	glAccountModel: GLAccountModel = new GLAccountModel();
	subTotal: number = 0;
	vat: number = 0;
	totalEstAmount: number = 0;
	totalMilestoneAmount: number | null = null;
	isAttachmentUploaded: boolean = false;
}

export class ContractLineFinYearDetailByCompanyWide {
	financialYearId: number | null = null;
	departments: ContractLineDetailByDeptModel[] = [];
	// Required for FE
	financialYear: string = "";
	qty: number | null = null;
	estUnitCost: number | null = null;
	vat: number | null = null;
	subTotal: number | null = null;
	totalEstAmount: number | null = null;
	contractLineDetailId: number | null = null;
	isCopy: boolean = false;
}

export class ContractLineModelByCompanyWide extends BaseContractLineDetail {
	companyWideDepartmentId: number | null = null;
	fromFinancialYear: string = "";
	toFinancialYear: string = "";
	contractLineDetailList: ContractLineFinYearDetailByCompanyWide[] = [];
	// Required for FE
	glAccountDetail: GLAccountDetailModel = new GLAccountDetailModel();
	itemUomName?: string = "";
	contractQty: number | null = null;
	contractEstUnitCost: number | null = null;
	contractVat: number = 0;
	contractSubTotal: number = 0;
	contractTotalEstCost: number = 0;
	// isDeleted: boolean = false;
}

// Contract line Save / Update - company wide
export class ContractLineDetailByCompanyWide {
	financialYearId: number = -1;
	qty: number | null = null;
	estUnitCost: number | null = null;
	contractLineDetailId: number | null = null;
}

export class ContractCompanyWideLineModel {
	contractLineId: number | null = null;
	contractId: number = -1;
	programmeId: number | null = null;
	requestingDepartmentId: number | null = null;
	buyingDepartmentId: number | null = null;
	requiredDate: Date = new Date();
	itemId: number | null = null;
	itemAvailabilityId: number | null = null;
	productDescription: string = "";
	glAccountId: number = -1;
	fromFinancialYearId: number | null = null;
	toFinancialYearId: number | null = null;
	qty: number | null = null;
	estUnitCost: number | null = null;
	csdCommodityId: number | null = null;
	companyWideDepartmentId: number | null = null;
	regionId: number | null = null;
	divisionId: number | null = null;
	contractLineDetailList: ContractLineDetailByCompanyWide[] = [];
	// isDeleted: boolean = false;
}

export interface IContractItemExistModel {
	contractId: number;
	itemId: number;
	buyingDepartmentId: number;
	csdCommodityId: number;
	productDescription: string;
	contractLineId: number;
	isCompanyWideItem: boolean;
	projectId: number | null;
	costCategoryId: number | null;
}
